import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import { ArrowRight } from "lucide-react";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import { isPerfectoStorefront, isBoxyStorefront } from "@/lib/storefrontTheme";

export default function StorefrontCategories({ config }) {
  const { t } = useTranslation();
  const { slug, base } = useStoreBase();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const primary = config.branding?.primary_color || "#15803D";

  useEffect(() => {
    api.get(`/store/${slug}/categories`)
      .then(({ data }) => setCategories(data))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SeoMeta endpoint={`/seo/${slug}/meta`} fallback={{ title: `Categories · ${config.store_info?.name || slug}` }} />
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: primary }}>{t("store.browse")}</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-stone-900">{t("store.shopByCategory")}</h1>
        <p className="text-stone-500 text-sm mt-2">{t("store.exploreCollections")}</p>
      </div>

      {loading ? (
        <div className="text-center text-stone-500 py-16">{t("common.loading")}</div>
      ) : categories.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-4xl mb-3">🗂️</div>
          <div className="font-display text-lg font-semibold text-stone-900">{t("categories.empty")}</div>
          <p className="text-stone-500 text-sm mt-1">This store hasn&#39;t added any categories.</p>
        </div>
      ) : (
        <div className={`grid grid-cols-2 ${isPerfectoStorefront(config) || isBoxyStorefront(config) ? "md:grid-cols-4 gap-3 sm:gap-4" : "sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6"}`}>
          {categories.filter((c) => !c.parent_id).map((c) => (
            <Link
              key={c.id}
              to={`${base}/shop?category=${c.slug}`}
              className={`group relative overflow-hidden bg-stone-100 ${isBoxyStorefront(config) ? "aspect-[3/4]" : isPerfectoStorefront(config) ? "aspect-[3/4] rounded-md" : "aspect-[4/5] rounded-3xl border border-stone-200 shadow-[0_2px_14px_-4px_rgba(0,0,0,0.08)]"}`}
              data-testid={`categories-tile-${c.slug}`}
            >
              {c.image_url ? (
                <img src={mediaUrl(c.image_url)} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ background: `${primary}18` }}>
                  <div className="font-display text-4xl font-bold" style={{ color: primary }}>{c.name.slice(0, 2).toUpperCase()}</div>
                </div>
              )}
              {isBoxyStorefront(config) ? (
                <>
                  <div className="absolute inset-0 bg-black/15" />
                  <div className="absolute inset-x-2 bottom-5 text-center font-display text-white text-sm uppercase tracking-[0.14em] drop-shadow">{c.name}</div>
                </>
              ) : isPerfectoStorefront(config) ? (
                <>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                  <div className="absolute left-3 bottom-3 text-white font-semibold">{c.name}</div>
                  <span className="absolute right-3 bottom-3 w-8 h-8 rounded-full border border-white/80 text-white flex items-center justify-center group-hover:bg-white group-hover:text-stone-900 transition-colors">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </>
              ) : (
                <>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="font-display text-lg font-bold">{c.name}</div>
                    <div className="text-xs opacity-80 flex items-center gap-1 mt-1 group-hover:gap-2 transition-all">Shop now <ArrowRight className="w-3.5 h-3.5" /></div>
                  </div>
                </>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
